import { useEffect, useId, useState, useSyncExternalStore, type ButtonHTMLAttributes, type ReactElement, type ReactNode, type SyntheticEvent } from "react";
import type { TooltipOverlay } from "../../overlays/Tooltip/TooltipOverlay.tsx";

/**
 * § Primitives, Button, IconButton and PlainButton, the disabled reason (2026-10-10): a button that is
 * disabled with a reason is not disabled natively, because a natively disabled button takes no focus
 * and fires no pointer event, so nothing it could say reaches anyone; it is `aria-disabled`, stays in
 * the focus order and under the pointer, and its reason opens as its tooltip on hover and on keyboard
 * focus and is its accessible description, through `aria-describedby` naming a hidden span of the
 * reason inside the button, joined to any description the host names. What it must not do is run.
 *
 * `withoutActivation` replaces the handlers through which a host, or a Radix trigger wrapping the
 * button as its child (a menu's or a popover's, which opens on the pointer going down or on a key),
 * runs it: every one is dropped, and the click, which `Enter`, `Space` and a form's implicit
 * submission all arrive as, is swallowed with its default and its propagation, so a submit button
 * submits nothing and a row or card the button sits in does not take the click as its own.
 *
 * `useDisabledReason` is the whole rule for a button that draws itself (`Button`, and `PlainButton`
 * for the controls a screen styles): the attributes to spread, the hidden note to render inside the
 * button, and the tooltip to stand the button in. The tooltip is a chunk, not a static import,
 * because the public entry (sign-in, setup) never loads Radix and a static import carried the whole
 * Radix chunk into it, 183.8 KB against its 135 KB budget (`scripts/size.ts`). A reason carries no
 * shortcut, so the chunk is the tip alone, `TooltipOverlay`, not `Tooltip` with its key hint
 * (2026-10-22): the key hint is in the first load of both entries through `Button`, and a lazily
 * loaded chunk that also depended on it kept `Button` and `ErrorState` out of the shared chunk. The authenticated entry
 * loads the chunk before it draws (`loadDisabledReasonTooltip`, awaited in `main.tsx`), so a button
 * whose `disabledReason` is given at all, a reason or `null` for none at the moment, stands in its
 * tooltip from its mount whether or not it is disabled, the tip held shut while it is enabled or has
 * no reason; the button's element, and with it the focus, survive its disabled state or its reason
 * coming and going, and no button is remounted when the chunk arrives. A button whose prop is
 * `undefined` carries no tooltip. Where the chunk was not loaded ahead (a public entry, a component
 * test), a button takes its tooltip the first time it is disabled with a reason, loading the chunk
 * then, and keeps it from then on. A tip Radix asked to open while the button was enabled, or left
 * open as it became enabled, is shut again rather than kept for the next time the button is disabled,
 * so no reason appears with nobody hovering or focusing it.
 */
function swallow(event: SyntheticEvent<HTMLButtonElement>): void {
  event.preventDefault();
  event.stopPropagation();
}

export function withoutActivation(props: ButtonHTMLAttributes<HTMLButtonElement>): ButtonHTMLAttributes<HTMLButtonElement> {
  return { ...props, onClick: swallow, onPointerDown: undefined, onPointerUp: undefined, onMouseDown: undefined, onMouseUp: undefined, onKeyDown: undefined, onKeyUp: undefined };
}

/** The host's description and the reason's hidden span, whichever are there, as one `aria-describedby`. */
export function describedByOf(host: string | undefined, reason: string | undefined): string | undefined {
  const ids = [host, reason].filter((id) => id !== undefined);
  return ids.length === 0 ? undefined : ids.join(" ");
}

type ReasonTooltip = typeof TooltipOverlay;

let loadedTooltip: ReasonTooltip | null = null;
let loadingTooltip: Promise<ReasonTooltip> | null = null;
const listeners = new Set<() => void>();

/** Loads the tooltip's chunk once and tells every mounted button; a load that fails is tried again by the next button that needs it. */
export function loadDisabledReasonTooltip(): Promise<ReasonTooltip> {
  loadingTooltip ??= import("../../overlays/Tooltip/TooltipOverlay.tsx").then(
    (module) => {
      loadedTooltip = module.TooltipOverlay;
      for (const listener of listeners) listener();
      return module.TooltipOverlay;
    },
    (error: unknown) => {
      loadingTooltip = null;
      throw error;
    },
  );
  return loadingTooltip;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function snapshot(): ReasonTooltip | null {
  return loadedTooltip;
}

export interface DisabledReasonInput {
  readonly disabled: boolean;
  /** A loading button is natively disabled while it works, whatever its reason. */
  readonly loading?: boolean;
  /** The reason, `null` for none at the moment, or `undefined` for a button that never has one. */
  readonly reason: string | null | undefined;
  /** The description the host names, which the reason's joins. */
  readonly describedBy: string | undefined;
  /** Everything else the host hands the button, dropped of its activation while the button is unavailable. */
  readonly props: ButtonHTMLAttributes<HTMLButtonElement>;
}

export interface DisabledReason {
  /** Disabled with a reason: `aria-disabled` and focusable rather than natively disabled. */
  readonly unavailable: boolean;
  /** The host's props with the button's `disabled`, `aria-disabled` and `aria-describedby`. */
  readonly props: ButtonHTMLAttributes<HTMLButtonElement>;
  /** The hidden span of the reason, rendered inside the button. */
  readonly note: ReactNode;
  /** Stands the button in the reason's tooltip once it has one, shut while the button is enabled. */
  readonly withTip: (control: ReactElement) => ReactElement;
}

export function useDisabledReason({ disabled, loading = false, reason, describedBy, props }: DisabledReasonInput): DisabledReason {
  const id = useId();
  const [open, setOpen] = useState(false);
  const Tip = useSyncExternalStore(subscribe, snapshot, snapshot);
  const why = reason ?? undefined;
  const unavailable = disabled && !loading && why !== undefined;
  // In the tooltip from the mount when a reason or `null` was given and the chunk is in; else from the first time the button is unavailable, and from then on.
  const [tipped, setTipped] = useState(() => reason !== undefined && Tip !== null);
  if (!tipped && unavailable) setTipped(true);
  if (open && !unavailable) setOpen(false);
  useEffect(() => {
    if (tipped && Tip === null) loadDisabledReasonTooltip().catch(() => undefined);
  }, [tipped, Tip]);
  return {
    unavailable,
    props: {
      ...(unavailable ? withoutActivation(props) : props),
      disabled: !unavailable && (disabled || loading),
      "aria-disabled": disabled || loading ? true : undefined,
      "aria-describedby": describedByOf(describedBy, unavailable ? id : undefined),
    },
    note:
      unavailable ? (
        <span id={id} hidden>
          {why}
        </span>
      ) : null,
    withTip: (control) =>
      tipped && Tip !== null ? (
        <Tip content={why} open={unavailable && open} onOpenChange={setOpen}>
          {control}
        </Tip>
      ) : (
        control
      ),
  };
}
